import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactSortable } from "react-sortablejs";
import { Form, Row, Col } from "react-bootstrap";
import styled from "styled-components";

import SntDialog from "@/components/SntDialog/SntDialog";
import SntCheckBox from "@cleanup/components/SntCheckBox/SntCheckBox";
import SntCloseIcon from "@icons/SntCloseIcon";
import SntReorderIcon from "@icons/SntReorderIcon";
import IcomoonIcon from "@icons/IcomoonIcon";
import SntLink from "@/components/ReactBootstrap/SntLink";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import SensolusOnlyBadge from "@/cleanup/widgets/SensolusOnlyBadge";

import DateFilter from "@cleanup/components/AdvancedFilters/DateFilter/DateFilter";
import MultiCheckboxFilter from "@cleanup/components/AdvancedFilters/MultiCheckboxFilter/MultiCheckboxFilter";
import MultiSelectAutocompleteFilter from "@cleanup/components/AdvancedFilters/MultiSelectAutocompleteFilter/MultiSelectAutocompleteFilter";
import BoundFilter from "@cleanup/components/AdvancedFilters/BoundFilter/BoundFilter";
import StringFilter from "@cleanup/components/AdvancedFilters/StringFilter/StringFilter";
import MultiStringFilter from "@cleanup/components/AdvancedFilters/MultiStringFilter/MultiStringFilter";
import NumericFilter from "@cleanup/components/AdvancedFilters/NumericFilter/NumericFilter";
import VersionFilter from "@cleanup/components/AdvancedFilters/VersionFilter/VersionFilter";
import BooleanFilter from "@cleanup/components/AdvancedFilters/BooleanFilter/BooleanFilter";

interface SearchField {
  _idx: string;
  key: string;
  label?: string;
  description?: string;
  filterType: string;
  searchType?: string;
  filterTypeSettings?: Record<string, unknown>;
  categoryId?: string;
  systemOnly?: boolean;
  [key: string]: unknown;
}

interface CategoryDescriptor {
  categoryID: string;
  categoryLabel: string;
  searchFields?: SearchField[];
}

interface FilterParam {
  filterKey: string;
  filterType: string;
  categoryId?: string;
  searchType?: string;
  filterValue?: Record<string, unknown>;
  notFilter?: boolean;
  [key: string]: unknown;
}

interface ConfigureFiltersModalProps {
  isShow: boolean;
  descriptors?: CategoryDescriptor[];
  visibleIdxs?: string[];
  defaultVisibleIdxs?: string[];
  appliedFilterSettings?: Record<string, FilterParam>;
  focusFilterIdx?: string | null;
  onApply: (
    visibleIdxs: string[],
    appliedFilterSettings: Record<string, FilterParam>
  ) => void;
  onClose: () => void;
  deviceCategorySuggestion?: string;
  disabled?: boolean;
  /** See MultiCheckboxFilter.orgId — passed straight through to the filters. */
  orgId?: number | null;
}

class FilterRowErrorBoundary extends React.Component<
  { children: React.ReactNode; label: string },
  { hasError: boolean; message: string }
> {
  constructor(props: { children: React.ReactNode; label: string }) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error.message || String(error) };
  }

  componentDidCatch(error: Error) {
    console.error(
      `[ConfigureFiltersModal] error rendering filter "${this.props.label}":`,
      error
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <span style={{ color: "#a52a2a", fontStyle: "italic" }}>
          ⚠ {this.props.label}: {this.state.message}
        </span>
      );
    }
    return this.props.children;
  }
}

class ModalErrorBoundary extends React.Component<
  { children: React.ReactNode; onClose: () => void },
  { hasError: boolean; message: string; stack: string }
> {
  constructor(props: { children: React.ReactNode; onClose: () => void }) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      message: error.message || String(error),
      stack: error.stack || "",
    };
  }

  componentDidCatch(error: Error) {
    console.error("[ConfigureFiltersModal] crash:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4">
          <h5 style={{ color: "#a52a2a" }}>⚠ The filter modal crashed</h5>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: "12px" }}>
            {this.state.message}
            {"\n\n"}
            {this.state.stack}
          </pre>
          <button className="btn btn-secondary" onClick={this.props.onClose}>
            Close
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ConfigLayout = styled.div`
  display: flex;
  height: 100%;
  gap: 1rem;
  min-height: 400px;

  .pane {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 0;
  }

  /*
   * min-width: the filter value panels open inside this pane and are clipped by
   * it (see .pane-body), so it has to stay wide enough to show one — FilterBody
   * declares min-width: 310px, plus room for the widest row of controls.
   */
  .pane:first-child {
    flex: 3;
    min-width: 360px;
    border-right: 1px solid #d5d9e0;
  }

  .pane:last-child {
    flex: 7;
  }

  .pane-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #d5d9e0;
    font-weight: 600;
    color: #424b59;
  }

  /*
   * Scrolls vertically, which makes it clip horizontally too — CSS computes
   * overflow-x: visible to auto next to an overflow-y that is not visible, so a
   * filter popup cannot escape this box. usePositionFilter keeps popups inside
   * it rather than flipping them out past its left edge.
   */
  .pane-body {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 1.5rem;
  }

  .selected-row {
    padding: 0.75rem 0.25rem;
    border-bottom: 1px solid #eef0f2;
    transition: background 0.15s ease;
  }
  .selected-row:last-child {
    border-bottom: none;
  }

  .selected-row.focus {
    background: #dae5ec;
    border-radius: 4px;
  }

  .selected-row .row-header {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .selected-row .drag-handle {
    margin-top: 0.4rem;
    color: #b9c0ca;
    cursor: grab;
    flex-shrink: 0;
  }

  .selected-row .row-action {
    margin-top: 0.4rem;
    flex-shrink: 0;
    cursor: pointer;
  }

  .selected-row .filter-editor {
    flex-grow: 1;
    min-width: 0;
  }

  .selected-row .filter-editor .mb-2 {
    margin-bottom: 6px !important;
  }

  /* SntSearch sets an inline width of 300px, which pushes the drag handle out of the row */
  .selected-row .filter-editor .snt-search-box {
    width: 250px !important;
  }

  .available-category {
    margin-bottom: 1rem;
  }

  .available-category-title {
    font-weight: 600;
    margin-bottom: 0.4rem;
    color: #424b59;
  }

  .available-row {
    padding: 0.45rem 0.25rem;
    border-bottom: 1px solid #eef0f2;
    cursor: pointer;
  }

  .available-row:hover {
    background: #f6f8fa;
  }

  .available-row.highlighted {
    background: #dae5ec;
  }

  .available-row-desc {
    color: #424b59;
  }

  .empty-state {
    text-align: center;
    color: #6c757d;
    padding: 2rem 1rem;
    font-style: italic;
  }
`;

const findIdxForFilterParam = (
  param: FilterParam,
  idxToField: Record<string, SearchField>
): string | null => {
  for (const idx of Object.keys(idxToField)) {
    const desc = idxToField[idx];
    const paramKey =
      (param as { key?: string }).key !== undefined
        ? (param as { key?: string }).key
        : param.filterKey;
    if (desc.key === paramKey && desc.searchType === param.searchType) {
      return idx;
    }
  }
  return null;
};

const ConfigureFiltersModal: React.FC<ConfigureFiltersModalProps> = ({
  isShow,
  descriptors,
  visibleIdxs,
  defaultVisibleIdxs,
  appliedFilterSettings,
  focusFilterIdx,
  onApply,
  onClose,
  deviceCategorySuggestion,
  disabled = false,
  orgId = null,
}) => {
  const [draftVisible, setDraftVisible] = useState<string[]>([]);
  const [draftSettings, setDraftSettings] = useState<
    Record<string, FilterParam>
  >({});
  const [searchText, setSearchText] = useState("");
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const focusRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const safeDescriptors = useMemo(
    () => (Array.isArray(descriptors) ? descriptors : []),
    [descriptors]
  );

  const idxToField = useMemo(() => {
    const map: Record<string, SearchField & { categoryLabel?: string }> = {};
    for (let i = 0; i < safeDescriptors.length; i++) {
      const cat = safeDescriptors[i];
      const fields = cat?.searchFields;
      if (!Array.isArray(fields)) continue;
      for (let j = 0; j < fields.length; j++) {
        const field = fields[j];
        if (!field) continue;
        const idx = field._idx || `descriptor_${i}_${j}`;
        map[idx] = {
          ...field,
          _idx: idx,
          categoryId: cat.categoryID,
          categoryLabel: cat.categoryLabel,
        };
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeDescriptors, isShow]);

  useEffect(() => {
    if (!isShow) return;
    setDraftVisible(Array.isArray(visibleIdxs) ? [...visibleIdxs] : []);
    setDraftSettings(
      appliedFilterSettings ? { ...appliedFilterSettings } : {}
    );
    setSearchText("");
    setHighlightedIdx(0);
    const t = setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isShow, visibleIdxs, appliedFilterSettings]);

  useEffect(() => {
    if (isShow && focusFilterIdx && focusRef.current) {
      const t = setTimeout(() => {
        focusRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [isShow, focusFilterIdx]);

  const _getDefaultApplyFilter = (idx: string): FilterParam => {
    const desc = idxToField[idx];
    if (!desc) {
      return { filterKey: "", filterType: "", notFilter: false };
    }
    return {
      categoryId: desc.categoryId || "",
      filterKey: desc.key,
      filterType: desc.filterType,
      searchType: desc.searchType || "",
      notFilter: false,
    };
  };

  const onAddFilter = (idx: string) => {
    setDraftVisible((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
    setDraftSettings((prev) => ({
      ...prev,
      [idx]: prev[idx] || _getDefaultApplyFilter(idx),
    }));
  };

  const onRemoveFilter = (idx: string) => {
    setDraftVisible((prev) => prev.filter((i) => i !== idx));
    setDraftSettings((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const onResetToDefaults = () => {
    const defaults = Array.isArray(defaultVisibleIdxs) ? defaultVisibleIdxs : [];
    setDraftVisible(defaults);
    const next: Record<string, FilterParam> = {};
    for (const idx of defaults) {
      next[idx] = _getDefaultApplyFilter(idx);
    }
    setDraftSettings(next);
  };

  const onReorder = (next: { id: string }[]) => {
    setDraftVisible(next.map((n) => n.id));
  };

  const onUpdateFilterItem = (incoming: unknown) => {
    if (
      typeof incoming === "object" &&
      incoming !== null &&
      "data" in incoming &&
      (incoming as { data: unknown }).data === null
    ) {
      const idxData = incoming as { idx: string; data: null };
      setDraftSettings((prev) => ({
        ...prev,
        [idxData.idx]: _getDefaultApplyFilter(idxData.idx),
      }));
    } else {
      const param = incoming as FilterParam;
      const matched = findIdxForFilterParam(param, idxToField);
      if (matched) {
        setDraftSettings((prev) => ({ ...prev, [matched]: param }));
      }
    }
  };

  const onSave = () => {
    onApply(draftVisible, draftSettings);
    onClose();
  };

  const renderFilterComponent = (descriptor: SearchField, data: FilterParam) => {
    const safeData =
      data && typeof data === "object" && data.filterKey
        ? data
        : _getDefaultApplyFilter(descriptor._idx);

    const common = {
      key: descriptor._idx,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      descriptor: descriptor as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: safeData as any,
      disabled,
      orgId,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange: onUpdateFilterItem as any,
    };

    switch (descriptor.filterType) {
      case "STRING":
        return <StringFilter {...common} />;
      case "MULTI_STRING":
        return <MultiStringFilter {...common} />;
      case "NUMERIC":
        return <NumericFilter {...common} />;
      case "VERSION":
        return <VersionFilter {...common} />;
      case "BOOLEAN":
        return <BooleanFilter {...common} />;
      case "DATE":
        return <DateFilter {...common} />;
      case "MULTI_SELECT_CHECKBOX":
        return (
          <MultiCheckboxFilter
            {...common}
            deviceCategorySuggestion={deviceCategorySuggestion}
          />
        );
      case "MULTI_SELECT_AUTO_COMPLETE":
        return <MultiSelectAutocompleteFilter {...common} />;
      case "SPATIAL":
        return <BoundFilter {...common} />;
      default:
        return (
          <span className="text-muted">
            {descriptor.label || descriptor.description || descriptor.key}
            <SensolusOnlyBadge systemOnly={descriptor.systemOnly} />
          </span>
        );
    }
  };

  const availableByCategory = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return safeDescriptors
      .map((cat) => {
        const fields = Array.isArray(cat?.searchFields)
          ? cat.searchFields
          : [];
        return {
          id: cat?.categoryID ?? "",
          name: cat?.categoryLabel ?? "",
          fields: fields.filter((f) => {
            if (!f || !f._idx) return false;
            if (draftVisible.includes(f._idx)) return false;
            if (!search) return true;
            const haystack = (
              (f.label || "") +
              " " +
              (f.description || "")
            ).toLowerCase();
            return haystack.includes(search);
          }),
        };
      })
      .filter((cat) => cat.fields.length > 0);
  }, [safeDescriptors, draftVisible, searchText]);

  const renderableIdxs = useMemo(
    () => draftVisible.filter((idx) => idxToField[idx]),
    [draftVisible, idxToField]
  );

  const flatAvailableIdxs = useMemo(() => {
    const list: string[] = [];
    for (const cat of availableByCategory) {
      for (const f of cat.fields) list.push(f._idx);
    }
    return list;
  }, [availableByCategory]);

  useEffect(() => {
    if (highlightedIdx >= flatAvailableIdxs.length) {
      setHighlightedIdx(Math.max(0, flatAvailableIdxs.length - 1));
    }
  }, [flatAvailableIdxs.length, highlightedIdx]);

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (flatAvailableIdxs.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIdx((i) => (i + 1) % flatAvailableIdxs.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIdx(
        (i) => (i - 1 + flatAvailableIdxs.length) % flatAvailableIdxs.length
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = flatAvailableIdxs[highlightedIdx];
      if (idx) onAddFilter(idx);
    }
  };

  const sortableList = useMemo(
    () => renderableIdxs.map((id) => ({ id })),
    [renderableIdxs]
  );

  return (
    <SntDialog
      isShow={isShow}
      onSave={onSave}
      onClose={onClose}
      onHide={onSave}
      title={
        renderableIdxs.length > 0
          ? `${translate(LanguageKey.configure_filters_key)} (${renderableIdxs.length} selected)`
          : translate(LanguageKey.configure_filters_key)
      }
      saveTxt={translate(LanguageKey.ok_key)}
      closeTxt={translate(LanguageKey.cancel_key)}
      customModalClassname="snt-configure-filters-modal"
      bodyClass="p-3"
    >
      {() => (
        <ModalErrorBoundary onClose={onClose}>
        <ConfigLayout>
          <div className="pane">
            <div className="pane-header d-flex justify-content-between align-items-center">
              <span>
                <span className="fw-bold">{draftVisible.length}</span>{" "}
                {translate(LanguageKey.filters_selected_key)}
              </span>
              {renderableIdxs.length > 0 && (
                <SntLink onClick={onResetToDefaults} style={{fontWeight: "normal"}}>
                  {translate(LanguageKey.reset_to_defaults_key)}
                </SntLink>
              )}
            </div>
            <div className="pane-body">
              <div className="text-muted mb-2 small">
                {translate(LanguageKey.filters_selected_info_key)}
              </div>

              {renderableIdxs.length === 0 ? (
                <div className="empty-state">
                  {translate(LanguageKey.no_filters_selected_key)}
                </div>
              ) : (
                <ReactSortable
                  animation={150}
                  list={sortableList}
                  setList={onReorder}
                  handle=".drag-handle"
                >
                  {renderableIdxs.map((idx) => {
                    const descriptor = idxToField[idx];
                    const data =
                      draftSettings[idx] || _getDefaultApplyFilter(idx);
                    const isFocus = focusFilterIdx === idx;
                    const labelText =
                      descriptor.label ||
                      descriptor.description ||
                      descriptor.key ||
                      "";
                    return (
                      <div
                        key={idx}
                        ref={isFocus ? focusRef : null}
                        className={`selected-row ${isFocus ? "focus" : ""}`}
                      >
                        <div className="row-header">
                          <div className="filter-editor">
                            <FilterRowErrorBoundary label={labelText}>
                              {renderFilterComponent(descriptor, data)}
                            </FilterRowErrorBoundary>
                          </div>
                          {descriptor.description &&
                            descriptor.description !== labelText && (
                              <IcomoonIcon
                                icon="info"
                                size={18}
                                title={descriptor.description}
                              />
                            )}
                          <SntReorderIcon
                            className="drag-handle"
                            title={translate(LanguageKey.drag_drop_order_key)}
                          />
                          <SntCloseIcon
                            className="row-action remove-filter"
                            title={translate(LanguageKey.remove_filter_key)}
                            onClick={() => onRemoveFilter(idx)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </ReactSortable>
              )}
            </div>
          </div>

          <div className="pane">
            <div className="pane-header">
              {translate(LanguageKey.available_fields_key)}
            </div>
            <div className="pane-body">
              <Form.Control
                ref={searchInputRef as unknown as React.Ref<HTMLInputElement>}
                placeholder={translate(LanguageKey.search_filters_key)}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setHighlightedIdx(0);
                }}
                onKeyDown={onSearchKeyDown}
                className="mb-3"
              />
              {availableByCategory.length === 0 ? (
                <div className="empty-state">—</div>
              ) : (
                availableByCategory.map((cat) => (
                  <div key={cat.id} className="available-category">
                    <div className="available-category-title">{cat.name}</div>
                    {cat.fields.map((field) => {
                      const labelText =
                        field.label || field.description || field.key;
                      const showDescription =
                        field.description && field.description !== labelText;
                      const isHighlighted =
                        flatAvailableIdxs[highlightedIdx] === field._idx;
                      return (
                        <div
                          key={field._idx}
                          className={`available-row ${isHighlighted ? "highlighted" : ""}`}
                        >
                          <Row>
                            <Col xs={6} xl={3}>
                              <SntCheckBox
                                checked={false}
                                onChange={() => onAddFilter(field._idx)}
                                label={
                                  <span className="ps-1">
                                    {labelText}
                                    <SensolusOnlyBadge
                                      systemOnly={field.systemOnly}
                                    />
                                  </span>
                                }
                              />
                            </Col>
                            <Col xs={6} xl={8}>
                              {showDescription && (
                                <div className="available-row-desc d-flex">
                                  <div style={{ minWidth: 24 }}>
                                    <IcomoonIcon icon="info" size={18} />
                                  </div>
                                  <div className="flex-grow-1">
                                    {field.description}
                                  </div>
                                </div>
                              )}
                            </Col>
                          </Row>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </ConfigLayout>
        </ModalErrorBoundary>
      )}
    </SntDialog>
  );
};

export default ConfigureFiltersModal;
